import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Search, Edit, Trash2, Eye, MapPin, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { SettingsSurface } from "@/features/settings/components/SettingsSurface";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { PermissionGate } from "@/features/auth/components/PermissionGate";
import { branchesAPI } from "@/lib/api";
import { Loading } from "@/components/ui/loader";
import { Switch } from "@/components/ui/switch";
import BranchLocationMap from "../../branches/components/BranchLocationMap";
import { confirmAction } from "@/components/ui/confirm-dialog";
import { formatDate } from "@/lib/utils";
import { EmptyState } from "@/components/ui/empty-state";
import { usePermission } from "@/hooks/usePermission";

interface Branch {
	id: string;
	name: string;
	nameEn?: string;
	status: "active" | "inactive";
	address?: string;
	city?: string;
	phone?: string;
	latitude?: number | null;
	longitude?: number | null;
	maxOrderDistanceKm?: number;
	employeeCount: number;
	createdAt: string;
	isMain?: boolean;
}

interface CreateBranchDto {
	name: string;
	nameEn?: string;
	status: "active" | "inactive";
	address?: string;
	city?: string;
	phone?: string;
	latitude?: number | null;
	longitude?: number | null;
	maxOrderDistanceKm?: number;
	isMain?: boolean;
}

const DEFAULT_CENTER = { lat: 24.7136, lng: 46.6753 }; // Riyadh

/** Branches counters, filters, table and CRUD dialogs — the former BranchesPage as a section. */
export default function BranchesSection() {
	const { t } = useTranslation();
	const { toast } = useToast();
	const canCreateBranches = usePermission("branches.create");

	const [branches, setBranches] = useState<Branch[]>([]);
	const [loading, setLoading] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");
	const [statusFilter, setStatusFilter] = useState<string>("all");
	const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
	const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
	const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
	const [defaultCenter, setDefaultCenter] = useState<{ lat: number; lng: number }>(DEFAULT_CENTER);
	const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
	const [formData, setFormData] = useState<CreateBranchDto>({
		name: "",
		nameEn: "",
		status: "active",
		address: "",
		city: "",
		phone: "",
		latitude: null,
		longitude: null,
		maxOrderDistanceKm: 50,
		isMain: false,
	});

	useEffect(() => {
		fetchBranches();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const fetchBranches = async () => {
		try {
			setLoading(true);
			const response = await branchesAPI.getAll();
			setBranches(response);
		} catch (error) {
			toast({
				title: t("common.error"),
				description: t("branches.errors.fetchFailed"),
				variant: "destructive",
			});
		} finally {
			setLoading(false);
		}
	};

	const handleCreate = async () => {
		try {
			await branchesAPI.create(formData);
			toast({ title: t("common.success"), description: t("branches.messages.created") });
			setIsCreateDialogOpen(false);
			resetForm();
			fetchBranches();
		} catch (error) {
			toast({
				title: t("common.error"),
				description: t("branches.errors.createFailed"),
				variant: "destructive",
			});
		}
	};

	const handleUpdate = async () => {
		if (!selectedBranch) return;
		try {
			await branchesAPI.update(selectedBranch.id, formData);
			toast({ title: t("common.success"), description: t("branches.messages.updated") });
			setIsEditDialogOpen(false);
			resetForm();
			fetchBranches();
		} catch (error) {
			toast({
				title: t("common.error"),
				description: t("branches.errors.updateFailed"),
				variant: "destructive",
			});
		}
	};

	const handleDelete = async (id: number | string) => {
		if (!(await confirmAction(t("branches.confirmations.delete"), { confirmLabel: t("common.delete") }))) return;
		try {
			await branchesAPI.delete(id);
			toast({ title: t("common.success"), description: t("branches.messages.deleted") });
			fetchBranches();
		} catch (error) {
			toast({
				title: t("common.error"),
				description: t("branches.errors.deleteFailed"),
				variant: "destructive",
			});
		}
	};

	const resetForm = () => {
		setFormData({
			name: "",
			nameEn: "",
			status: "active",
			address: "",
			city: "",
			phone: "",
			latitude: null,
			longitude: null,
			maxOrderDistanceKm: 50,
			isMain: false,
		});
		setSelectedBranch(null);
	};

	const openEditDialog = (branch: Branch) => {
		setSelectedBranch(branch);
		setFormData({
			name: branch.name,
			nameEn: branch.nameEn || "",
			status: branch.status,
			address: branch.address || "",
			city: branch.city || "",
			phone: branch.phone || "",
			latitude: branch.latitude ?? null,
			longitude: branch.longitude ?? null,
			maxOrderDistanceKm: branch.maxOrderDistanceKm ?? 50,
			isMain: branch.isMain || false,
		});
		setDefaultCenter({ lat: branch.latitude ?? DEFAULT_CENTER.lat, lng: branch.longitude ?? DEFAULT_CENTER.lng });
		setIsEditDialogOpen(true);
	};

	const openViewDialog = (branch: Branch) => {
		setSelectedBranch(branch);
		setDefaultCenter({ lat: branch.latitude ?? DEFAULT_CENTER.lat, lng: branch.longitude ?? DEFAULT_CENTER.lng });
		setIsViewDialogOpen(true);
	};

	const openLocationOnGoogleMaps = (latitude: number | null, longitude: number | null) => {
		if (!latitude || !longitude) return;
		window.open(`https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`, "_blank");
	};

	const totalBranches = branches.length;
	const activeBranches = branches.filter(b => b.status === "active").length;
	const inactiveBranches = branches.filter(b => b.status === "inactive").length;

	const filteredBranches = branches.filter(branch => {
		const matchesSearch =
			branch.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
			branch.nameEn?.toLowerCase().includes(searchTerm.toLowerCase());
		const matchesStatus = statusFilter === "all" || branch.status === statusFilter;
		return matchesSearch && matchesStatus;
	});

	return (
		<div className="space-y-4">
			{/* Counters + filters + add in one toolbar */}
			<div className="flex flex-wrap items-center gap-2">
				<div className="flex items-center gap-2 px-1 py-2.5">
					<span className="text-sm text-muted-foreground">{t("branches.summary.total")}</span>
					<span className="text-sm font-semibold">{totalBranches}</span>
				</div>
				<div className="flex items-center gap-2 px-1 py-2.5">
					<span className="h-2 w-2 rounded-full bg-emerald-500" />
					<span className="text-sm text-muted-foreground">{t("branches.summary.active")}</span>
					<span className="text-sm font-semibold">{activeBranches}</span>
				</div>
				<div className="flex items-center gap-2 px-1 py-2.5">
					<span className="h-2 w-2 rounded-full bg-muted-foreground/40" />
					<span className="text-sm text-muted-foreground">{t("branches.summary.inactive")}</span>
					<span className="text-sm font-semibold">{inactiveBranches}</span>
				</div>
				<div className="ms-auto flex flex-wrap items-center gap-2">
					<div className="relative w-full sm:w-auto">
						<Search className="absolute start-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
						<Input
							aria-label={t("branches.filters.searchPlaceholder")}
							placeholder={t("branches.filters.searchPlaceholder")}
							value={searchTerm}
							onChange={e => setSearchTerm(e.target.value)}
							className="h-9 w-full sm:w-48 ps-8"
						/>
					</div>
					<Select value={statusFilter} onValueChange={setStatusFilter}>
						<SelectTrigger className="h-9 w-32" aria-label={t("branches.fields.status")}>
							<SelectValue>{t(`common.${statusFilter}`)}</SelectValue>
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">{t("common.all")}</SelectItem>
							<SelectItem value="active">{t("common.active")}</SelectItem>
							<SelectItem value="inactive">{t("common.inactive")}</SelectItem>
						</SelectContent>
					</Select>
					<PermissionGate permission="branches.create">
						<Button size="sm" className="h-9" onClick={() => setIsCreateDialogOpen(true)}>
							<Plus className="w-4 h-4 me-1" />
							{t("branches.actions.add")}
						</Button>
					</PermissionGate>
				</div>
			</div>

			{loading ? (
				<Loading />
			) : (
				<div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
					{filteredBranches.length === 0 && (
						<div className="col-span-full">
							{searchTerm.trim() || statusFilter !== "all" ? (
								<EmptyState icon={Search} title={t("common.noResults")} />
							) : (
								<EmptyState
									icon={Building2}
									title={t("branches.empty.title")}
									action={
										canCreateBranches
											? {
													label: t("branches.actions.add"),
													onClick: () => setIsCreateDialogOpen(true),
												}
											: undefined
									}
								/>
							)}
						</div>
					)}
					{filteredBranches.map(branch => (
						<SettingsSurface key={branch.id} className="flex flex-col gap-4">
							<div className="flex items-start gap-3">
								<div className="rounded-lg bg-primary/10 p-3 text-primary">
									<Building2 className="h-5 w-5" />
								</div>
								<div className="min-w-0 flex-1">
									<h3 className="text-base font-semibold">{branch.name}</h3>
									{branch.nameEn && branch.nameEn !== branch.name && (
										<p className="mt-1 text-sm text-muted-foreground">{branch.nameEn}</p>
									)}
									<p className="mt-1 text-sm text-muted-foreground">
										{[branch.city, branch.address].filter(Boolean).join(" · ")}
									</p>
								</div>
								<Badge variant={branch.status === "active" ? "success" : "secondary"}>
									{t(`common.${branch.status}`)}
								</Badge>
							</div>
							<dl className="settings-definition-grid">
								<div>
									<dt>{t("branches.table.headers.number")}</dt>
									<dd>
										<bdi>#{branch.id}</bdi>
									</dd>
								</div>
								<div>
									<dt>{t("branches.table.headers.employeeCount")}</dt>
									<dd>{branch.employeeCount ?? 0}</dd>
								</div>
								<div className="col-span-2">
									<dt>{t("branches.table.headers.createdAt")}</dt>
									<dd>{formatDate(branch.createdAt)}</dd>
								</div>
							</dl>
							<Button
								variant="ghost"
								className="justify-start px-0"
								onClick={() =>
									openLocationOnGoogleMaps(branch.latitude ?? null, branch.longitude ?? null)
								}
							>
								<MapPin className="me-2 h-4 w-4" />
								{t("branches.fields.viewOnMap")}
							</Button>
							<div className="mt-auto flex flex-wrap justify-end gap-2 border-t pt-4">
								<PermissionGate permission="branches.read">
									<Button
										variant="ghost"
										size="icon"
										aria-label={t("common.view")}
										onClick={() => openViewDialog(branch)}
									>
										<Eye className="h-4 w-4" />
									</Button>
								</PermissionGate>
								<PermissionGate permission="branches.update">
									<Button variant="outline" onClick={() => openEditDialog(branch)}>
										<Edit className="me-2 h-4 w-4" />
										{t("common.edit")}
									</Button>
								</PermissionGate>
								<PermissionGate permission="branches.delete">
									<Button
										variant="ghost"
										size="icon"
										aria-label={t("common.delete")}
										onClick={() => handleDelete(branch.id)}
									>
										<Trash2 className="h-4 w-4 text-destructive" />
									</Button>
								</PermissionGate>
							</div>
						</SettingsSurface>
					))}
				</div>
			)}

			{/* Create Branch Dialog */}
			<Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
				<DialogContent className="max-w-5xl">
					<DialogHeader>
						<DialogTitle>{t("branches.dialogs.create.title")}</DialogTitle>
						<DialogDescription>{t("branches.dialogs.create.description")}</DialogDescription>
					</DialogHeader>
					<div className="space-y-1 w-full">
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
							<div className="space-y-1">
								<Label htmlFor="name">{t("branches.fields.name")} *</Label>
								<Input
									id="name"
									value={formData.name}
									onChange={e => setFormData({ ...formData, name: e.target.value })}
									placeholder={t("branches.fields.namePlaceholder")}
								/>
							</div>
							<div className="space-y-1">
								<Label htmlFor="nameEn">{t("branches.fields.nameEn")}</Label>
								<Input
									id="nameEn"
									value={formData.nameEn}
									onChange={e => setFormData({ ...formData, nameEn: e.target.value })}
									placeholder={t("branches.fields.nameEnPlaceholder")}
								/>
							</div>
							<div className="space-y-1">
								<Label htmlFor="status">{t("branches.fields.status")}</Label>
								<Select
									value={formData.status}
									onValueChange={(value: "active" | "inactive") =>
										setFormData({ ...formData, status: value })
									}
								>
									<SelectTrigger aria-label={t("branches.fields.status")}>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="active">{t("common.active")}</SelectItem>
										<SelectItem value="inactive">{t("common.inactive")}</SelectItem>
									</SelectContent>
								</Select>
							</div>
							<div className="space-y-1">
								<Label htmlFor="phone">{t("branches.fields.phone")}</Label>
								<Input
									id="phone"
									value={formData.phone}
									onChange={e => setFormData({ ...formData, phone: e.target.value })}
									placeholder={t("branches.fields.phonePlaceholder")}
									dir="ltr"
								/>
							</div>
							<div className="space-y-1">
								<Label htmlFor="address">{t("branches.fields.address")}</Label>
								<Input
									id="address"
									value={formData.address}
									onChange={e => setFormData({ ...formData, address: e.target.value })}
									placeholder={t("branches.fields.addressPlaceholder")}
								/>
							</div>
							<div className="space-y-1">
								<Label htmlFor="city">{t("branches.fields.city")}</Label>
								<Input
									id="city"
									value={formData.city}
									onChange={e => setFormData({ ...formData, city: e.target.value })}
									placeholder={t("branches.fields.cityPlaceholder")}
								/>
							</div>
							<div className="space-y-1">
								<Label htmlFor="maxKm">{t("branches.fields.maxOrderDistanceKm")}</Label>
								<Input
									id="maxKm"
									type="number"
									min={0.5}
									step={0.5}
									value={formData.maxOrderDistanceKm ?? 50}
									onChange={e =>
										setFormData({
											...formData,
											maxOrderDistanceKm: parseFloat(e.target.value) || 50,
										})
									}
									dir="ltr"
								/>
							</div>
							<div className="space-y-1 mt-2">
								<div className="flex items-center gap-2 border border-border rounded-md p-3">
									<Switch
										id="isMain"
										checked={formData.isMain}
										onCheckedChange={(value: boolean) =>
											setFormData({ ...formData, isMain: value })
										}
									/>
									<Label htmlFor="isMain" className="cursor-pointer">
										{t("branches.fields.isMain")}
									</Label>
								</div>
							</div>
						</div>
						<div className="space-y-1 w-full">
							<Label>{t("branches.fields.location")}</Label>
							<BranchLocationMap
								latitude={formData.latitude}
								longitude={formData.longitude}
								defaultCenter={defaultCenter}
								className="w-full"
								onChange={(lat, lng) => setFormData({ ...formData, latitude: lat, longitude: lng })}
								readOnly={false}
							/>
							{(formData.latitude == null || formData.longitude == null) && (
								<p className="text-sm text-warning dark:text-amber-200 bg-warning/10 dark:bg-amber-950/40 rounded-md p-3">
									{t("branches.warnings.menuCoordinates")}
								</p>
							)}
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
							{t("common.cancel")}
						</Button>
						<Button onClick={handleCreate}>{t("branches.actions.create")}</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Edit Branch Dialog */}
			<Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
				<DialogContent className="max-w-5xl">
					<DialogHeader>
						<DialogTitle>{t("branches.dialogs.edit.title")}</DialogTitle>
						<DialogDescription>{t("branches.dialogs.edit.description")}</DialogDescription>
					</DialogHeader>
					<div className="space-y-1">
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
							<div className="space-y-1">
								<Label htmlFor="edit-name">{t("branches.fields.name")} *</Label>
								<Input
									id="edit-name"
									value={formData.name}
									onChange={e => setFormData({ ...formData, name: e.target.value })}
									placeholder={t("branches.fields.namePlaceholder")}
								/>
							</div>
							<div className="space-y-1">
								<Label htmlFor="edit-nameEn">{t("branches.fields.nameEn")}</Label>
								<Input
									id="edit-nameEn"
									value={formData.nameEn}
									onChange={e => setFormData({ ...formData, nameEn: e.target.value })}
									placeholder={t("branches.fields.nameEnPlaceholder")}
								/>
							</div>
							<div className="space-y-1">
								<Label htmlFor="edit-status">{t("branches.fields.status")}</Label>
								<Select
									value={formData.status}
									onValueChange={(value: "active" | "inactive") =>
										setFormData({ ...formData, status: value })
									}
								>
									<SelectTrigger aria-label={t("branches.fields.status")}>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="active">{t("common.active")}</SelectItem>
										<SelectItem value="inactive">{t("common.inactive")}</SelectItem>
									</SelectContent>
								</Select>
							</div>
							<div className="space-y-1">
								<Label htmlFor="edit-phone">{t("branches.fields.phone")}</Label>
								<Input
									id="edit-phone"
									value={formData.phone}
									onChange={e => setFormData({ ...formData, phone: e.target.value })}
									placeholder={t("branches.fields.phonePlaceholder")}
									className="rtl:text-right"
									dir="ltr"
								/>
							</div>
							<div className="space-y-1">
								<Label htmlFor="edit-address">{t("branches.fields.address")}</Label>
								<Input
									id="edit-address"
									value={formData.address}
									onChange={e => setFormData({ ...formData, address: e.target.value })}
									placeholder={t("branches.fields.addressPlaceholder")}
								/>
							</div>
							<div className="space-y-1">
								<Label htmlFor="edit-city">{t("branches.fields.city")}</Label>
								<Input
									id="edit-city"
									value={formData.city}
									onChange={e => setFormData({ ...formData, city: e.target.value })}
									placeholder={t("branches.fields.cityPlaceholder")}
								/>
							</div>
							<div className="space-y-1">
								<Label htmlFor="edit-maxKm">{t("branches.fields.maxOrderDistanceKm")}</Label>
								<Input
									id="edit-maxKm"
									type="number"
									min={0.5}
									step={0.5}
									value={formData.maxOrderDistanceKm ?? 50}
									onChange={e =>
										setFormData({
											...formData,
											maxOrderDistanceKm: parseFloat(e.target.value) || 50,
										})
									}
								/>
							</div>
							<div className="space-y-1 mt-2">
								<div className="flex items-center gap-2 border border-border rounded-md p-3">
									<Switch
										id="edit-isMain"
										checked={formData.isMain}
										onCheckedChange={(value: boolean) =>
											setFormData({ ...formData, isMain: value })
										}
									/>
									<Label htmlFor="edit-isMain" className="cursor-pointer">
										{t("branches.fields.isMain")}
									</Label>
								</div>
							</div>
						</div>
						<div className="space-y-1 w-full">
							<Label>{t("branches.fields.location")}</Label>
							<BranchLocationMap
								latitude={formData.latitude}
								longitude={formData.longitude}
								defaultCenter={defaultCenter}
								onChange={(lat, lng) => setFormData({ ...formData, latitude: lat, longitude: lng })}
								readOnly={false}
								className="w-full"
							/>
							{(formData.latitude == null || formData.longitude == null) && (
								<p className="text-sm text-warning dark:text-amber-200 bg-warning/10 dark:bg-amber-950/40 rounded-md p-3">
									{t("branches.warnings.menuCoordinates")}
								</p>
							)}
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
							{t("common.cancel")}
						</Button>
						<Button onClick={handleUpdate}>{t("common.update")}</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* View Branch Dialog */}
			<Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
				<DialogContent className="max-w-5xl">
					<DialogHeader>
						<DialogTitle>{t("branches.dialogs.view.title")}</DialogTitle>
						<DialogDescription>{t("branches.dialogs.view.description")}</DialogDescription>
					</DialogHeader>
					{selectedBranch && (
						<div className="space-y-4">
							<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
								<div className="space-y-1">
									<Label className="text-muted-foreground">{t("branches.fields.name")}</Label>
									<p className="font-medium">{selectedBranch.name}</p>
									{selectedBranch.nameEn && (
										<p className="text-sm text-muted-foreground">{selectedBranch.nameEn}</p>
									)}
								</div>
								<div className="space-y-1">
									<Label className="text-muted-foreground">{t("branches.fields.status")}</Label>
									<Badge variant={selectedBranch.status === "active" ? "success" : "warning"}>
										{selectedBranch.status === "active" ? t("common.active") : t("common.inactive")}
									</Badge>
								</div>
								<div className="space-y-1">
									<Label className="text-muted-foreground">{t("branches.fields.phone")}</Label>
									<p dir="ltr" className="rtl:text-right">
										{selectedBranch.phone || "-"}
									</p>
								</div>
								<div className="space-y-1 md:col-span-2">
									<Label className="text-muted-foreground">{t("branches.fields.address")}</Label>
									<p>{selectedBranch.address || "-"}</p>
								</div>
								<div className="space-y-1">
									<Label className="text-muted-foreground">{t("branches.fields.city")}</Label>
									<p>{selectedBranch.city || "-"}</p>
								</div>
								<div className="space-y-1">
									<Label className="text-muted-foreground">
										{t("branches.fields.maxOrderDistanceKm")}
									</Label>
									<p>{selectedBranch.maxOrderDistanceKm ?? 50}</p>
								</div>
							</div>
							<div className="space-y-1 w-full">
								<Label className="text-muted-foreground">{t("branches.fields.location")}</Label>
								<BranchLocationMap
									latitude={selectedBranch.latitude}
									longitude={selectedBranch.longitude}
									defaultCenter={defaultCenter}
									readOnly
									className="w-full"
								/>
							</div>
							<DialogFooter>
								<Button variant="outline" onClick={() => setIsViewDialogOpen(false)}>
									{t("common.close")}
								</Button>
								<PermissionGate permission="branches.update">
									<Button
										onClick={() => {
											setIsViewDialogOpen(false);
											openEditDialog(selectedBranch);
										}}
									>
										{t("common.edit")}
									</Button>
								</PermissionGate>
							</DialogFooter>
						</div>
					)}
				</DialogContent>
			</Dialog>
		</div>
	);
}
